<template>
  <div>
    <b-loading :is-full-page="false" :active="loading" />
    <div class="page-wrapper content-wrapper">
      <!-- Case Information -->
      <div class="horizontal-section">
        <div class="left-section">
          <div style="width: 100%;">
            <div class="content-title-wrapper">
              <label class="hierarchy-label">{{ $t('case-information') }}</label>
            </div>
            <div class="form-wrapper">
              <!-- <b-field :label="$t('priority')" horizontal>
                <span class="priority-badge">{{ projectData.priority }}</span>
              </b-field> -->

              <b-field :label="$t('case-id')" horizontal>
                <span class="field-text">{{ projectData.caseId }}</span>
              </b-field>

              <b-field :label="$t('status')" horizontal>
                <span class="field-text">{{ getStatusName(information.status) }}</span>
              </b-field>

              <b-field :label="$t('accession-date')" horizontal>
                <span class="field-text">{{ projectData.accessionDate }}</span>
              </b-field>
            </div>
          </div>
            
          <!-- Details -->
          <div class="panel">
            <div style="width: 100%;">
              <div class="content-title-wrapper">
                <label class="hierarchy-label">{{ $t('details') }}</label>
              </div>
              <div class="form-wrapper">
                <b-field :label="$t('patient-id')" horizontal>
                  <span class="field-text">{{ patient.id }}</span>
                </b-field>
                
                <b-field :label="$t('patient-his-id')" horizontal>
                  <span class="field-text">{{ patient.hisid }}</span>
                </b-field>

                <b-field :label="$t('patient-name')" horizontal>
                  <span class="field-text">{{ patient.name }}</span>
                </b-field>

                <b-field :label="$t('gender-age')" horizontal>
                  <span class="field-text">{{ getGenderLabel(patient.gender) }} / {{ getFullAge(patient.birth) }}Y </span>
                </b-field>

                <b-field :label="$t('date-of-birth')" horizontal>
                  <span class="field-text">{{ formattedBirth }}</span>
                </b-field>

                <b-field :label="$t('description')" horizontal>
                  <cytomine-description :object="project" :canEdit="canManageProject" />
                </b-field>

                <b-field :label="$t('specimen-type')" horizontal>
                  <span class="field-text">{{ information.specimen_type || 'No Data' }}</span>
                </b-field>

                <b-field :label="$t('anatomic-site')" horizontal>
                  <span class="field-text">{{ information.anatomic_site || 'No Data' }}</span>
                </b-field>

                <b-field :label="$t('bench')" horizontal>
                  <span class="field-text">{{ information.bench || 'No Data' }}</span>
                </b-field>
              </div>
            </div>
          </div>

          <!-- Image & Annotation Details -->
          <div class="panel">
            <div style="width: 100%;">
              <div class="content-title-wrapper">
                <label class="hierarchy-label">{{ $t('image-annotation-details') }}</label>
              </div>
              <div class="form-wrapper">
                <b-field :label="$t('number-of-images')" horizontal>
                  <span class="field-text">{{ projectData.numberOfImages }}</span>
                </b-field>

                <b-field :label="$t('manual-annotations')" horizontal>
                  <div class="field-text">
                    <div>{{ projectData.manualAnnotations.regionsOfInterest }}</div>
                    <div>{{ projectData.manualAnnotations.cellCounts }}</div>
                  </div>
                </b-field>

                <b-field :label="$t('reviewed-annotations')" horizontal>
                  <span class="field-text">{{ projectData.reviewedAnnotations }}</span>
                </b-field>
              </div>
            </div>
          </div>

          <!-- Related Entities & Metadata -->
          <div class="panel">
            <div style="width: 100%;">
              <div class="content-title-wrapper">
                <label class="hierarchy-label">{{ $t('related-entities-metadata') }}</label>
              </div>
              <div class="form-wrapper">
                <b-field :label="$t('members')" horizontal>
                  <div class="items-container">
                    <div v-for="member in members" :key="member.id">
                      <span class="field-text">{{ member.firstname || '' }} {{ member.lastname || '' }}</span>
                    </div>
                  </div>
                </b-field>

                <b-field :label="$t('tags')" horizontal>
                  <div class="field-text">
                    <cytomine-tags :object="project" :canEdit="canManageProject" />
                  </div>
                </b-field>

                <b-field :label="$t('properties')" horizontal>
                  <div class="field-text">
                    <cytomine-properties :object="project" :canEdit="canManageProject" />
                  </div>
                </b-field>

                <!-- <b-field :label="$t('ontology')" horizontal>
                  <router-link v-if="project.ontology" :to="`/ontology/${project.ontology}`">
                    {{project.ontologyName}}
                  </router-link>
                  <em v-else>{{$t('no-ontology')}}</em>
                </b-field> -->
              </div>
            </div>
          </div>

          <!-- Files & Personnel -->
          <div class="panel">
            <div style="width: 100%;">
              <div class="content-title-wrapper">
                <label class="hierarchy-label">{{ $t('files-personnel') }}</label>
              </div>
              <div class="form-wrapper">
                <b-field :label="$t('attached-files')" horizontal>
                  <attached-files :object="project" :canEdit="canManageProject" />
                </b-field>

                <b-field :label="$t('creator')" horizontal>
                  <span class="field-text">{{ creator?.firstname || '' }} {{ creator?.lastname || '' }}</span>
                </b-field>

                <b-field :label="$t('representatives')" horizontal>
                  <div class="items-container">
                    <div v-for="rep in representatives" :key="rep.id">
                      <span class="field-text">{{ rep.firstname || '' }} {{ rep.lastname || '' }}</span>
                    </div>
                  </div>
                </b-field>

                <b-field :label="$t('managers')" horizontal>
                  <div class="items-container">
                    <div v-for="manager in managers" :key="manager.id">
                      <span class="field-text">{{ manager.firstname || '' }} {{ manager.lastname || '' }}</span>
                    </div>
                  </div>
                </b-field>

                <!-- <b-field :label="$t('consultation-required-status')" horizontal>
                  <span class="field-text">{{ projectData.consultationRequired }}</span>
                </b-field> -->

                <b-field :label="$t('final-report-generated')" horizontal>
                  <span class="field-text">  {{ information.diagnosis_finalized_date ? formatTimestamp(information.diagnosis_finalized_date) : 'No Data' }}</span>
                </b-field>
              </div>
              
            </div>
          </div>
        </div>


        <div class="right-section">
          <!-- <list-annotations /> -->
          <list-annotations-user />
          <!-- <list-annotations /> -->


          <div class="external-analysis-table">
            <div class="summary-list-panel">
              <h2>외부 분석 작업 목록 ({{ externResult.collection ? externResult.collection.length : 0 }}건)</h2>
              
              <div v-if="externResult.collection && externResult.collection.length > 0">
                <table class="result-table summary">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>플랫폼</th>
                      <th>상태</th>
                      <th>생성일</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      v-for="(item, index) in externResult.collection" 
                      :key="item.externTaskId"
                      @click="selectTask(item)"
                      :class="{'is-selected': selectedTask && selectedTask.externTaskId === item.externTaskId}"
                    >
                      <td>{{ (externResult.offset || 0) + index + 1 }}</td>
                      <td>
                        <span class="platform-name">{{ item.externPlatformName }}</span>
                      </td>
                      <td>
                        <span :class="['task-status', item.externTaskStatus.toLowerCase()]">
                          {{ item.externTaskStatus }}
                        </span>
                      </td>
                      <td>
                        <span class="date-text">{{ formatTimestamp(item.taskCreated) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div v-else class="no-results">
                <p>현재 프로젝트에 연결된 외부 분석 결과가 없습니다.</p>
              </div>
            </div>

            <div class="detail-view-panel">
              <h3>상세 결과</h3>

              <div v-if="selectedTask" class="task-detail-content">
                <b-field label="파일 정보" horizontal>
                  <span class="field-text filename">{{ selectedTask.abstractImageOriginalFilename }}</span>
                </b-field>
                
                <b-field label="상태" horizontal>
                  <span :class="['field-text', 'task-status', selectedTask.externTaskStatus.toLowerCase()]">
                    {{ selectedTask.externTaskStatus }}
                  </span>
                </b-field>

                <b-field label="요약" horizontal>
                  <span class="field-text summary-text">{{ selectedTask.externResultSummary }}</span>
                </b-field>

                <b-field label="상세 내용" horizontal>
                  <p class="field-text detail-text">{{ selectedTask.externResultDetail }}</p>
                </b-field>

                <b-field label="플랫폼 메시지" horizontal>
                  <p class="field-text message-text">{{ selectedTask.externResultMessage }}</p>
                </b-field>

                <list-annotations-algo />

              </div>
              
              <div v-else class="detail-placeholder">
                <p>목록에서 작업을 선택하여 상세 결과를 확인하세요.</p>
              </div>
            </div>


          </div>
        </div>

      </div>
    </div>
  </div>
</template>
  
<script>
import {get} from '@/utils/store-helpers';
import dayjs from 'dayjs';
import DefaultWorklist from '../../enums/DefaultWorklist';

import CytomineDescription from '@/components/description/CytomineDescription';
import CytomineProperties from '@/components/property/CytomineProperties';
import CytomineTags from '@/components/tag/CytomineTags';
import AttachedFiles from '@/components/attached-file/AttachedFiles';

import { GenderEnum } from '../../enums/DefaultEnums';
// import ListAnnotations from '../annotations/ListAnnotations.vue';
import ListAnnotationsAlgo from '../annotations/ListAnnotationsAlgo.vue';
import ListAnnotationsUser from '../annotations/ListAnnotationsUser.vue';

export default {
  name: 'project-information-component',
  components: {
    CytomineDescription,
    CytomineProperties,
    CytomineTags,
    AttachedFiles,

    // ListAnnotations,
    ListAnnotationsUser,
    ListAnnotationsAlgo,
  },
  data() {
    return {
      loading: false,
      error: false,

      creator: null,
      managers: [],
      members: [],
      onlines: [],
      representatives: [],
      patient: {},
      information: {},
      externResult: {},

      // 💡 선택된 항목을 저장할 변수 추가
      selectedTask: null, 
    };
  },
  watch: {},
  computed: {
    currentUser: get('currentUser/user'),
    project: get('currentProject/project'),
    currentWorklist() {
      return this.$store.getters['worklist/getCurrentWorklist'];
    },
    projectData() {
      if (!this.project) {
        return {
          priority: '',
          caseId: '',
          status: '',
          accessionDate: '',
          numberOfImages: 0,
          manualAnnotations: {
            regionsOfInterest: '',
            cellCounts: ''
          },
          reviewedAnnotations: '',
          consultationRequired: '',
          finalReportGenerated: ''
        };
      }
      
      return {
        priority: this.project.priority || '',
        caseId: this.project.id || '',
        status: this.project.status || '',
        accessionDate: this.project.created ? this.formatTimestamp(this.project.created) : '',
        numberOfImages: this.project.numberOfImages || this.project.images || 0,
        manualAnnotations: {
          regionsOfInterest: this.project.numberOfAnnotations ? `${this.project.numberOfAnnotations} regions of interest` : 'No Data',
          cellCounts: ''
        },
        reviewedAnnotations: this.project.numberOfReviewedAnnotations ? `${this.project.numberOfReviewedAnnotations} regions of interest reviewed` : 'No Data',
        consultationRequired: this.project.consultationRequired || '',
        finalReportGenerated: this.project.finalReportGenerated || ''
      };
    },
    managersIds() {
      return this.managers.map(manager => manager.id);
    },
    isSuperAdmin() {
      return this.$store.getters['currentUser/isSuperAdmin'];
    },
    canManageProject() {
      return (
        this.currentUser.adminByNow || 
        this.isSuperAdmin || // SuperAdmin 추가
        this.managersIds.includes(this.currentUser.id)
      );
    },
    formattedBirth() {
      const birth = this.patient.birth;
      if (!birth) return '-';
      return dayjs(birth).format('YYYY-MM-DD');
    },
  },
  methods: {
    formatTimestamp(timestamp) {
      return dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss');
    },
    getStatusName(statusId) {
      return DefaultWorklist[statusId] || '';
    },
    
    async fetchCreator() {
      this.creator = await this.project.fetchCreator();
    },
    async fetchManagers() {
      this.managers = (await this.project.fetchAdministrators()).array;
    },
    async fetchRepresentatives() {
      this.representatives = (await this.project.fetchRepresentatives()).array;
    },
    async fetchMembers() {
      this.members = (await this.project.fetchUsers()).array;
    },
    async fetchOnlines() {
      this.onlines = await this.project.fetchConnectedUsers();
    },
    async fetchPatient() {
      this.patient = await this.project.fetchPatient();
    },
    async fetchInformation() {
      this.information = await this.project.fetchInfo('information');
    },
    async fetchExternResult() {
      this.externResult = await this.project.fetchExternResult();
    },
    sortAssociatedTags() {
      this.associatedTags.sort((a, b) => a.tagName.localeCompare(b.tagName));
    },
    getFullAge(birthTimestamp) {
      if (!birthTimestamp) {
        return 'Unknown';
      }

      const birthDate = new Date(birthTimestamp);

      if (Number.isNaN(birthDate.getTime())) {
        return 'Unknown';
      }

      const today = new Date();

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      if (!Number.isFinite(age) || age < 0 || age > 150) {
        return 'Unknown';
      }

      return age;
    },
    getGenderLabel(genderValue) {
      return GenderEnum[genderValue] || 'Unknown';
    },
    // 💡 선택된 작업 설정 메서드 추가
    selectTask(task) {
      this.selectedTask = task;
    },
    // 💡 formatHeatmap 메서드 추가
    formatHeatmap(jsonString) {
      if (!jsonString) return '';
      try {
        const jsonObject = JSON.parse(jsonString);
        return JSON.stringify(jsonObject, null, 2);
      }
      catch (e) {
        return jsonString;
      }
    },
  },
  async created() {
    await Promise.all([
      this.fetchCreator(),
      this.fetchManagers(),
      this.fetchRepresentatives(),
      this.fetchMembers(),
      this.fetchOnlines(),

      this.fetchPatient(),
      this.fetchInformation(),
      this.fetchExternResult(),
    ]);
  }
};

</script>

<style scoped lang="scss">
.page-wrapper {
  vertical-align: middle !important;
  height: 100%;
  overflow-y: auto;
}

.content-wrapper {
  padding: 0 2.5%;
  padding-bottom: 2rem;
  position: relative;
}

.content-title-wrapper {
  height: 75px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.horizontal-section {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
}

.left-section {
  margin-right: 5rem;
  width: 35%;
  box-sizing: border-box;
}
.right-section {
  margin-right: 5rem;
  width: 50%;
  box-sizing: border-box;
}

.section_item {
}

.panel {
  display: flex;
  flex-direction: row;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
}

.hierarchy-label {
  margin: 18px 10px;
  font-size: 1.3rem;
  color: #4A4847;
}

.form-wrapper {
  padding: 20px 30px;
}

.field-text {
  display: inline-block;
  color: #363636;
  font-size: 0.95rem;
  line-height: 1.5;
  word-wrap: break-word;
}

.priority-badge {
  display: inline-block;
  background-color: #ff3860;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 0.9rem;
  min-width: 30px;
  text-align: center;
}

.tag-item {
  display: inline-block;
  background-color: #3273dc;
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  margin-right: 8px;
  margin-bottom: 4px;
  font-size: 0.85rem;
}

.property-item {
  margin-bottom: 6px;
}

.property-item:last-child {
  margin-bottom: 0;
}

.file-item {
  padding: 4px 0;
  color: #3273dc;
  text-decoration: underline;
  cursor: pointer;
}

.file-item:hover {
  color: #1f5bb5;
}

.items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}





.external-analysis-table {
  /* 목록과 상세보기를 세로로 나열 */
  display: flex;
  flex-direction: column; 
  gap: 20px;
  margin-top: 15px;
}

.summary-list-panel, .detail-view-panel {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
}

.summary-list-panel h2, .detail-view-panel h3 {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
  margin-bottom: 10px;
  font-size: 1.1rem;
  font-weight: 600;
}

/* 목록 테이블 스타일 */
.result-table.summary {
  width: 100%;
}
.result-table.summary th, .result-table.summary td {
  padding: 8px 12px;
  font-size: 0.85rem;
  border: none;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

.result-table.summary tbody tr:hover {
  background-color: #f0f8ff; /* 호버 시 배경색 변경 */
}
.result-table.summary tbody tr.is-selected {
  background-color: #dbeaff; /* 선택된 항목 강조 */
  font-weight: bold;
}

/* 상세 보기 필드 스타일 */
.task-detail-content .b-field {
  margin-bottom: 10px;
}
.task-detail-content .field-text {
  font-size: 0.9rem;
}
.detail-placeholder {
  color: #888;
  text-align: center;
  padding: 30px 10px;
  border: 1px dashed #ccc;
  border-radius: 4px;
}

/* 기타 텍스트 및 뱃지 스타일 재사용 */
.platform-name { font-weight: 600; margin-right: 5px; }
.task-status { /* 기존 task-status 스타일 유지 */ }
.no-data { color: #aaa; font-style: italic; }
</style>
